// stressHeartRate.ts
/**
 * Stress test — percentage of age-predicted maximum heart rate.
 *
 * Dr Shashi, 31 Aug, in his own words:
 *
 *   "Need to add formula. 220 - age is 100%. So if we put this and apply the
 *    calculation, peak heart rate / 100% heart rate multiplied by 100 will give
 *    you percentage. 220 - age : 100% heart rate for any patient."
 *
 * So the denominator is the AGE-PREDICTED MAXIMUM (220 − age), NOT the protocol
 * target of 85% of predicted max. The distinction matters — the same patient
 * reads 88% against predicted max and 104% against an 85% target — and the
 * report body already committed to the same reading before this module existed:
 * "Heart rate increased from {{stress_resting_hr}} BPM to {{stress_peak_hr}} BPM
 * ({{stress_percent_predicted}}%) of the predicted maximum heart rate".
 *
 * WHY A DERIVATION AT ALL
 * The percentage was only ever imported, parsed out of a CardioSoft XML or PDF
 * (`PercentAchievedMaxPredicted` / "This value represents NN %"). Most studies
 * have no such file attached, so the slot sat at `--` and the sonographer had no
 * way to fill it except arithmetic in their head. What Dr Shashi asked for is the
 * opposite flow: "sono usually inputs peak HR hence, system automatically
 * calculates the percentage. This is done on both conclusion and body."
 *
 * WHAT THIS DELIBERATELY WILL NOT DO
 * Return a number it cannot stand behind. Every input that is missing, unparseable
 * or physiologically implausible yields `null`, and a `null` leaves whatever the
 * report already shows untouched rather than overwriting it with a guess or
 * blanking it. A stress report is a diagnostic document; a wrong percentage is
 * worse than an empty one the clinician can see needs filling.
 */

/** Key of the typed input that drives the derivation. */
export const PEAK_HR_KEY = 'stress_peak_hr';

/** Key of the derived value, printed in both the body and the conclusion. */
export const PERCENT_PREDICTED_KEY = 'stress_percent_predicted';

/** Key of the predicted maximum. Derived and kept consistent, but not printed. */
export const TARGET_HR_KEY = 'stress_target_hr';

/**
 * Plausible ages for the formula. Below 1 the arithmetic is meaningless; above
 * 120 the value is certainly a data error rather than a patient. Note that
 * 220 − age is an adult approximation in the first place — it is what the
 * treadmill software uses and what the clinician asked for, not a claim that it
 * is valid in paediatrics.
 */
const MIN_AGE = 1;
const MAX_AGE = 120;

/**
 * Plausible peak heart rates in BPM. A stress test that records 15 or 400 has a
 * transcription error, and propagating it into a percentage would launder a typo
 * into a clinical finding.
 */
const MIN_PEAK_HR = 30;
const MAX_PEAK_HR = 300;

/**
 * Leading number of a display string ("158 BPM" → 158, "--" → null).
 *
 * Mirrors `parseChainValue` in the measurement chain: a value a clinician has
 * replaced with prose ("not achieved") must stop the derivation, not be coerced
 * into a number.
 */
export function parseStressNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const match = /-?\d+(?:\.\d+)?/.exec(value.replace(/,/g, ''));
  if (!match) {
    return null;
  }
  const parsed = parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Patient age in whole years.
 *
 * `data.age` is what `buildPatientDemographics` puts on the report (from the
 * DICOM patient DOB), and 469 of the 471 stress reports on staging carry it. The
 * DOB fallback exists for the handful that do not, and for a report opened
 * without study metadata to hand.
 *
 * DOB is formatted DD/MM/YYYY on the report — Australian order. Parsing it with
 * `new Date(string)` would read 05/11/1955 as 11 May and silently shift the age
 * by up to a year, so the parts are read explicitly.
 */
export function resolveAge(source: Record<string, unknown> | null | undefined): number | null {
  if (!source) {
    return null;
  }

  const direct = parseStressNumber(source.age);
  if (direct !== null && direct >= MIN_AGE && direct <= MAX_AGE) {
    return Math.floor(direct);
  }

  const dob = source.dob;
  if (typeof dob !== 'string') {
    return null;
  }
  const match = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(dob.trim());
  if (!match) {
    return null;
  }
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const now = new Date();
  let age = now.getFullYear() - year;
  const monthDelta = now.getMonth() + 1 - month;
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < day)) {
    age -= 1;
  }
  return age >= MIN_AGE && age <= MAX_AGE ? age : null;
}

/**
 * Age-predicted maximum heart rate — the 100% reference. Dr Shashi's formula
 * verbatim: `220 − age`.
 */
export function predictedMaxHR(age: number | null): number | null {
  if (age === null || !Number.isFinite(age) || age < MIN_AGE || age > MAX_AGE) {
    return null;
  }
  return 220 - age;
}

/**
 * Percentage of age-predicted maximum HR achieved, rounded to a whole number.
 *
 * Whole numbers because that is how the existing reports read ("achieving 88% of
 * peak HR") and how CardioSoft prints it — a decimal place would imply a
 * precision that a peak heart rate read off a trace does not have.
 *
 * Returns null when it cannot be computed honestly. Callers must treat null as
 * "leave the report alone".
 */
export function percentPredictedHR(
  peakHRValue: unknown,
  age: number | null
): number | null {
  const peak = parseStressNumber(peakHRValue);
  if (peak === null || peak < MIN_PEAK_HR || peak > MAX_PEAK_HR) {
    return null;
  }
  const predicted = predictedMaxHR(age);
  if (predicted === null || predicted <= 0) {
    return null;
  }
  return Math.round((peak / predicted) * 100);
}

/**
 * Values the report should show once `stress_peak_hr` has been typed.
 *
 * Returned as display strings keyed by placeholder, ready to be written into the
 * matching numeric slots. Keys are omitted entirely when they cannot be derived,
 * so an incomplete study never blanks a value the report already has.
 *
 * `stress_target_hr` is derived even though Dr Shashi asked for it NOT to be
 * printed ("target HR does not need to be written"). Two clinic templates still
 * carry the placeholder today, and an imported target that disagrees with the
 * denominator actually used would make the printed percentage unverifiable.
 */
export function deriveStressHeartRateValues(
  peakHRValue: unknown,
  source: Record<string, unknown> | null | undefined
): Record<string, string> {
  const age = resolveAge(source);
  const derived: Record<string, string> = {};

  const predicted = predictedMaxHR(age);
  if (predicted !== null) {
    derived[TARGET_HR_KEY] = String(predicted);
  }

  const percent = percentPredictedHR(peakHRValue, age);
  if (percent !== null) {
    derived[PERCENT_PREDICTED_KEY] = String(percent);
  }

  return derived;
}
